import React, { useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { TextArea } from 'ncoded-component-library';
import { FieldRenderProps } from 'react-final-form';
import { ExtractPropTypes } from 'types';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './TextAreaField.styles.scss';

type InputProps = ExtractPropTypes<typeof TextArea>;

export type TextAreaFieldProps = FieldWrapperProps &
  InputProps &
  FieldRenderProps<string, HTMLElement> & {
    autoResize?: boolean;
    customOnBlur?: (e: React.FocusEvent, isValid?: boolean) => void;
  };

const TextAreaField: React.FC<TextAreaFieldProps> = (props) => {
  const {
    className,
    input: { value, name, onBlur, ...restInput },
    meta: { touched, error },
    lang = 'en',
    maxLength,
    customOnBlur,
    autoResize = false,
    ...rest
  } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasError = useMemo(() => touched && error, [error, touched]);

  useEffect(() => {
    if (!autoResize || !wrapperRef?.current) return;

    // Find the actual textarea element inside the wrapper
    const textArea = wrapperRef.current.querySelector('textarea');
    if (!textArea) return;

    // Reset height to auto to get the correct scrollHeight
    textArea.style.height = 'auto';
    // Set height to scrollHeight to fit content
    textArea.style.height = `${textArea.scrollHeight}px`;
  }, [value, autoResize]);

  const classes = classNames(
    'text-area-field',
    {
      'text-area-field--error': hasError,
      'text-area-field--auto-resize': autoResize,
    },
    className,
  );

  return (
    <FieldWrapper
      className={classes}
      maxLength={maxLength}
      error={error}
      hasError={hasError}
      name={name}
      value={value}
      {...restInput}
      {...rest}
    >
      <div ref={autoResize ? wrapperRef : undefined}>
        <TextArea
          hasError={hasError}
          maxLength={maxLength}
          lang={lang}
          name={name}
          value={value}
          onBlurCapture={onBlur}
          onBlur={(e) => customOnBlur?.(e, !hasError)}
          {...restInput}
          {...rest}
        />
      </div>
    </FieldWrapper>
  );
};

export default TextAreaField;
